import React, { useContext, useEffect, useMemo, useState } from 'react';
import {
  DownOutlined,
  FileTextOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import { Icon as LegacyIcon } from '@ant-design/compatible';
import {
  Button,
  Descriptions,
  Dropdown,
  Empty,
  Input,
  Menu,
  Space,
  Table,
  Tag,
} from 'antd';
import Highlighter from 'react-highlight-words';
import CreateClientAccountModal from './createClientAccount';
import { useQuery } from '@apollo/client';
import { FETCH_ALL_CLIENTS } from './queries';
import EditClientModal from './EditClientModal';
import {
  Client,
  GetAllClientsQueryResult,
  useGetAllClientsQuery,
} from '../../graphql/datamodel.gen';
import { getClient } from '../../apollo';
import {
  ClientsContext,
  useClientsContext,
} from '../../contexts/clientsContext';
import { SortOrder } from 'antd/lib/table/interface';
import { getTableScroll } from '../../utils/table';
import TemplatesModal from './TemplatesModal';

export default function ClientsContainer() {
  const { setFilteredInfo, filteredInfo, sortedInfo, setSortedInfo } =
    useContext(ClientsContext);

  const response = useGetAllClientsQuery({
    client: getClient(),
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'cache-and-network',
  });

  const { data, refetch, networkStatus } = response;

  const loading = networkStatus === 1;

  const clients = useMemo(() => {
    return data.clients || [];
  }, [data?.clients]);

  return (
    <ClientsView
      clients={clients}
      loading={loading && !data}
      refetch={refetch}
      filteredInfo={filteredInfo}
      setFilteredInfo={setFilteredInfo}
      setSortedInfo={setSortedInfo}
      sortedInfo={sortedInfo}
    />
  );
}

interface IClientViewProps {
  clients: NonNullable<GetAllClientsQueryResult['data']['clients']>;
  loading: boolean;
  refetch: any;
  filteredInfo: any;
  sortedInfo: any;
  setFilteredInfo: (filteredInfo: any) => void;
  setSortedInfo: (filteredInfo: any) => void;
}

interface IClientsViewState {
  searchText: string;
  searchedColumn: string;
  activeRecord: any;
  drawerVisible: boolean;
  editClientDrawerVisible: boolean;
  showTemplatesModal: boolean;
}

class ClientsView extends React.Component<IClientViewProps, IClientsViewState> {
  state = {
    searchText: '',
    searchedColumn: '',
    activeRecord: undefined,
    drawerVisible: false,
    editClientDrawerVisible: false,
    showTemplatesModal: false,
  };
  searchInput: any;

  handleChange = (pagination: any, filters: any, sorter: any) => {
    this.props.setSortedInfo(sorter);
    this.props.setFilteredInfo(filters);
  };

  handleSearch = (
    selectedKeys: any[],
    confirm: () => void,
    dataIndex: string,
  ) => {
    confirm();
    this.setState({ searchText: selectedKeys[0], searchedColumn: dataIndex });
  };

  handleReset = (clearFilters: () => void, confirm: () => void) => {
    clearFilters();
    confirm();
    this.setState({ searchText: '' });
  };

  showGiveAccessToClientDrawer = (record: any) => {
    this.setState({
      drawerVisible: true,
      activeRecord: record,
    });
  };

  hideGiveAccessToClientModal = async () => {
    this.setState({
      drawerVisible: false,
      activeRecord: undefined,
    });
    await this.props.refetch();
  };

  showEditClientDrawer = async (record) => {
    this.setState({
      editClientDrawerVisible: true,
      activeRecord: record,
    });
  };

  hideEditClientDrawer = async () => {
    this.setState({
      editClientDrawerVisible: false,
      activeRecord: undefined,
    });
    await this.props.refetch();
  };

  private clearAllFilters = () => {
    this.setState({
      searchText: '',
    });

    this.props.setFilteredInfo({});
    this.props.setSortedInfo({});
  };

  getColumnSearchProps = (dataIndex) => ({
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
    }) => (
      <div style={{ padding: 8 }}>
        <Input
          ref={(node) => {
            this.searchInput = node;
          }}
          placeholder={`Buscar`}
          value={selectedKeys[0]}
          onChange={(e) =>
            setSelectedKeys(e.target.value ? [e.target.value] : [])
          }
          onPressEnter={() =>
            this.handleSearch(selectedKeys, confirm, dataIndex)
          }
          style={{ marginBottom: 8, display: 'block' }}
        />
        <Space>
          <Button
            type="primary"
            onClick={() => this.handleSearch(selectedKeys, confirm, dataIndex)}
            icon={<SearchOutlined translate={undefined} />}
            size="small"
            style={{ width: 90 }}
          >
            Buscar
          </Button>
          <Button
            onClick={() => this.handleReset(clearFilters, confirm)}
            size="small"
            style={{ width: 90 }}
          >
            Limpiar
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered) => (
      <SearchOutlined
        style={{ color: filtered ? '#1890ff' : undefined }}
        translate={undefined}
      />
    ),
    onFilter: (value, record) => {
      const searchValue = dataIndex.split('.').reduce(function (a, b) {
        return a[b];
      }, record);

      return searchValue
        ? searchValue.toString().toLowerCase().includes(value.toLowerCase())
        : '';
    },
    onFilterDropdownVisibleChange: (visible) => {
      if (visible) {
        setTimeout(() => this.searchInput.select(), 100);
      }
    },
    render: (text) =>
      this.state.searchedColumn === dataIndex ? (
        <Highlighter
          highlightStyle={{ backgroundColor: '#ffc069', padding: 0 }}
          searchWords={[this.state.searchText]}
          autoEscape
          textToHighlight={text ? text.toString() : ''}
        />
      ) : (
        text
      ),
  });

  showTemplatesModal = () => {
    this.setState({
      showTemplatesModal: true,
    });
  };

  hideTemplatesModal = () => {
    this.setState({
      showTemplatesModal: false,
    });
  };

  render() {
    const sortedInfo = this.props.sortedInfo || {};
    const filteredInfo = this.props.filteredInfo || {};

    const columns = [
      {
        title: 'ID',
        dataIndex: 'id',
        key: 'id',
        filterDropdown: ({
          setSelectedKeys,
          selectedKeys,
          confirm,
          clearFilters,
        }: any): React.ReactNode => (
          <div style={{ padding: 8 }}>
            <Input
              ref={(node) => {
                this.searchInput = node;
              }}
              placeholder={`Buscar por ID`}
              value={selectedKeys[0]}
              onChange={(e) =>
                setSelectedKeys(e.target.value ? [e.target.value] : [])
              }
              onPressEnter={() =>
                this.handleSearch(selectedKeys, confirm, 'id')
              }
              style={{ width: 188, marginBottom: 8, display: 'block' }}
            />
            <Button
              type="primary"
              onClick={() => this.handleSearch(selectedKeys, confirm, 'id')}
              icon={<SearchOutlined translate={undefined} />}
              size="small"
              style={{ width: 90, marginRight: 8 }}
            >
              Buscar
            </Button>
            <Button
              onClick={() => this.handleReset(clearFilters, confirm)}
              size="small"
              style={{ width: 90 }}
            >
              Limpiar
            </Button>
          </div>
        ),
        filterIcon: (filtered: any): React.ReactNode => (
          <SearchOutlined
            style={{ color: filtered ? '#1890ff' : undefined }}
            translate={undefined}
          />
        ),
        onFilter: (value: string, record: any) => {
          return (record['id'] || '')
            .toString()
            .toLowerCase()
            .includes(value.toLowerCase());
        },
        onFilterDropdownVisibleChange: (visible: any) => {
          if (visible) {
            setTimeout(() => this.searchInput.select());
          }
        },
        render: (text: string, record: Client) => (
          <Highlighter
            highlightStyle={{ backgroundColor: '#ffc069', padding: 0 }}
            searchWords={[this.state.searchText]}
            autoEscape
            textToHighlight={record.volcanoClientId || record.id}
          />
        ),
      },
      {
        title: 'Nombre',
        dataIndex: 'name',
        key: 'name',
        sorter: (a: any, b: any) => {
          if (
            a.name &&
            b.name &&
            typeof a.name === 'string' &&
            typeof b.name === 'string'
          ) {
            return a.name < b.name ? -1 : a.name > b.name ? 1 : 0;
          }

          return 0;
        },
        sortOrder:
          (sortedInfo as any).columnKey === 'name' && (sortedInfo as any).order,
        filterDropdown: ({
          setSelectedKeys,
          selectedKeys,
          confirm,
          clearFilters,
        }: any): React.ReactNode => (
          <div style={{ padding: 8 }}>
            <Input
              ref={(node) => {
                this.searchInput = node;
              }}
              placeholder={`Buscar por nombre`}
              value={selectedKeys[0]}
              onChange={(e) =>
                setSelectedKeys(e.target.value ? [e.target.value] : [])
              }
              onPressEnter={() =>
                this.handleSearch(selectedKeys, confirm, 'name')
              }
              style={{ width: 188, marginBottom: 8, display: 'block' }}
            />
            <Button
              type="primary"
              onClick={() => this.handleSearch(selectedKeys, confirm, 'name')}
              icon={<SearchOutlined translate={undefined} />}
              size="small"
              style={{ width: 90, marginRight: 8 }}
            >
              Buscar
            </Button>
            <Button
              onClick={() => this.handleReset(clearFilters, confirm)}
              size="small"
              style={{ width: 90 }}
            >
              Limpiar
            </Button>
          </div>
        ),
        filterIcon: (filtered: any): React.ReactNode => (
          <SearchOutlined
            style={{ color: filtered ? '#1890ff' : undefined }}
            translate={undefined}
          />
        ),
        onFilter: (
          value: string,
          record: {
            name: string;
          },
        ) =>
          (record['name'] || '')
            .toString()
            .toLowerCase()
            .includes(value.toLowerCase()),
        onFilterDropdownVisibleChange: (visible: any) => {
          if (visible) {
            setTimeout(() => this.searchInput.select());
          }
        },
        render: (text: string) => (
          <Highlighter
            highlightStyle={{ backgroundColor: '#ffc069', padding: 0 }}
            searchWords={[this.state.searchText]}
            autoEscape
            textToHighlight={text?.toString()}
          />
        ),
      },
      {
        title: 'NIF',
        dataIndex: 'nif',
        key: 'nif',
        filterDropdown: ({
          setSelectedKeys,
          selectedKeys,
          confirm,
          clearFilters,
        }: any): React.ReactNode => (
          <div style={{ padding: 8 }}>
            <Input
              ref={(node) => {
                this.searchInput = node;
              }}
              placeholder={`Buscar por NIF`}
              value={selectedKeys[0]}
              onChange={(e) =>
                setSelectedKeys(e.target.value ? [e.target.value] : [])
              }
              onPressEnter={() =>
                this.handleSearch(selectedKeys, confirm, 'nif')
              }
              style={{ width: 188, marginBottom: 8, display: 'block' }}
            />
            <Button
              type="primary"
              onClick={() => this.handleSearch(selectedKeys, confirm, 'nif')}
              icon={<SearchOutlined translate={undefined} />}
              size="small"
              style={{ width: 90, marginRight: 8 }}
            >
              Buscar
            </Button>
            <Button
              onClick={() => this.handleReset(clearFilters, confirm)}
              size="small"
              style={{ width: 90 }}
            >
              Limpiar
            </Button>
          </div>
        ),
        filterIcon: (filtered: any): React.ReactNode => (
          <SearchOutlined
            style={{ color: filtered ? '#1890ff' : undefined }}
            translate={undefined}
          />
        ),
        onFilter: (value: string, record: any) => {
          if (!record.nif) return false;

          return (record['nif'] || '')
            .toString()
            .toLowerCase()
            .includes(value.toLowerCase());
        },
        onFilterDropdownVisibleChange: (visible: any) => {
          if (visible) {
            setTimeout(() => this.searchInput.select());
          }
        },
        render: (text: string) => (
          <Highlighter
            highlightStyle={{ backgroundColor: '#ffc069', padding: 0 }}
            searchWords={[this.state.searchText]}
            autoEscape
            textToHighlight={text?.toString()}
          />
        ),
      },
      {
        title: 'Acceso a Web Panel',
        dataIndex: 'hasWebPanelAccount',
        key: 'hasWebPanelAccount',
        sortDirections: ['descend', 'ascend'] as SortOrder[],
        // defaultSortOrder: 'descend' as 'descend' | 'ascend' | undefined,
        render: (_: any, record: any) => (
          <span>
            {(() => {
              let color;
              let localizedStatus = 'No';
              if (record.hasWebPanelAccount) {
                color = 'green';
                localizedStatus = 'Sí';
              }
              if (!record.hasWebPanelAccount) {
                color = 'volcano';
                localizedStatus = 'No';
              }
              return <Tag color={color}>{localizedStatus.toUpperCase()}</Tag>;
            })()}
          </span>
        ),
        filters: [
          { text: 'Sí', value: 'true' },
          { text: 'No', value: 'false' },
        ],
        // filteredValue: (filteredInfo as any).hasWebPanelAccount,
        onFilter: (filter: string, record: any) => {
          if (filter === 'true') {
            return record.hasWebPanelAccount === true;
          }

          if (filter === 'false') {
            return record.hasWebPanelAccount === false;
          }

          return true;
        },
        sorter: (a: any, b: any) => {
          const firstVal = a.hasWebPanelAccount ? 0 : 1;
          const secondVal = b.hasWebPanelAccount ? 0 : 1;

          return firstVal - secondVal;
        },
        sortOrder:
          (sortedInfo as any).columnKey === 'hasWebPanelAccount'
            ? (sortedInfo as any).order
            : 'ascend',
      },
      {
        title: 'Tarifario',
        key: 'rateId',
        render: (_, record) => {
          return record?.rate?.name;
        },
      },
      {
        title: 'Acciones',
        key: 'actions',
        render: (text: any, record: any) => {
          const actionsMenu = (
            <Menu>
              {!record.hasWebPanelAccount && (
                <Menu.Item
                  onClick={() => this.showGiveAccessToClientDrawer(record)}
                  key="create_webpanel_account"
                >
                  Crear cuenta en Web Panel
                </Menu.Item>
              )}
              {record.hasWebPanelAccount && (
                <Menu.Item
                  onClick={() => this.showEditClientDrawer(record)}
                  key="edit_client"
                >
                  Gestionar cliente
                </Menu.Item>
              )}
            </Menu>
          );
          return (
            <Dropdown overlay={actionsMenu}>
              <Button size="small">
                Acciones <DownOutlined translate={undefined} />
              </Button>
            </Dropdown>
          );
        },
      },
    ];

    return (
      <div>
        <div
          style={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'baseline',
            justifyContent: 'space-between',
            paddingBottom: '2em',
          }}
        >
          <div>
            <Button
              icon={<LegacyIcon type={'close'} />}
              style={{ marginRight: '10px' }}
              onClick={this.clearAllFilters}
            >
              Eliminar filtros
            </Button>
          </div>

          <div>
            <Button
              icon={<FileTextOutlined />}
              onClick={this.showTemplatesModal}
            >
              Plantillas de presupuesto
            </Button>
          </div>
        </div>
        <Table
          // sticky
          rowKey={(record) => record.id}
          locale={{
            emptyText: (
              <Empty
                description="No hay datos disponibles"
                image={Empty.PRESENTED_IMAGE_SIMPLE}
              />
            ),
          }}
          onChange={this.handleChange}
          loading={this.props.loading}
          size={'small'}
          pagination={{ pageSize: 25 }}
          columns={columns}
          dataSource={this.props.clients}
          // scroll={{ y: getTableScroll({ extraHeight: 150 + 20 }) }}
          expandedRowRender={(record: Client) => {
            return (
              <Descriptions size="small">
                <Descriptions.Item label="Teléfono">
                  {record.phone}
                </Descriptions.Item>
                <Descriptions.Item label="Email">
                  {record.email}
                </Descriptions.Item>
                <Descriptions.Item label="Email en Volcano">
                  {record.volcanoEmail}
                </Descriptions.Item>
              </Descriptions>
            );
          }}
        />

        <CreateClientAccountModal
          visible={this.state.drawerVisible}
          hideModal={this.hideGiveAccessToClientModal}
          record={this.state.activeRecord}
        />

        <EditClientModal
          volcanoClientId={this.state.activeRecord?.volcanoClientId}
          visible={this.state.editClientDrawerVisible}
          clientId={this.state.activeRecord?.id}
          hideDrawer={this.hideEditClientDrawer}
        />

        <TemplatesModal
          show={this.state.showTemplatesModal}
          onClose={this.hideTemplatesModal}
        />
      </div>
    );
  }
}
